import React, { useState, useEffect, useCallback } from "react";
import "../../static/css/App.css";
import { Container, Button, Modal, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../static/css/customerTable.css";
import moment from "moment";
import { Box } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { showConfirm } from "react-confirm-prompt";
import DynamicBreadcrumb from "../header/breadcrumb";
import productAPI from "../../services/productapi";
import Loader from "../loader/loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import ProductViewModal from "./ProductViewModal";
import "../../static/css/productManagement.css";
library.add(fas);

const ProductsList = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [totalProduct, setTotalProduct] = useState(0);
    const [refreshTable, setRefreshTable] = useState(false);
    const [sortModel, setSortModel] = useState([]);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 15,
    });
    const [openModal, setOpenModal] = useState(false);
    const [notesText, setNotesText] = useState("Enter Reason");
    const [errors, setErrors] = useState({});
    const [closeNotes, setCloseNotes] = useState("");
    const [productId, setProductId] = useState(null);
    const [invModal, setInvModal] = useState(false);
    const [productStatus, setProductStatus] = useState(null);
    const [loaderVisible, setLoaderVisible] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(0);

    const navigate = useNavigate();
    const pathArray = [
        {
            name: "Products List",
            url: null,
        },
    ];

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        setPaginationModel((current) => ({ ...current, page: 0 }));
    }, [debouncedSearch, statusFilter]);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;
            const data = await productAPI.getAllProducts({
                limit: pageSize,
                page,
                sortModel,
                search: debouncedSearch,
                status: statusFilter,
            });

            const formattedRows = data.data.result.map((cust, index) => ({
                id: cust.id,
                sno: page * pageSize + index + 1,
                pro_created: moment(cust.createdAt).format("YYYY/MM/DD kk:mm:ss"),
                name: cust.productName,
                brandName: cust.brandName,
                categoryName: cust.CategoryName,
                itemImage: cust.itemImages ?? "",
                status: cust.status,
            }));
            setRows(formattedRows);
            setTotalProduct(Number(data.data.totalRecords?.[0]?.total_records || 0));
        } catch (error) {
            console.error("Failed to fetch Product:", error);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, sortModel, refreshTable, debouncedSearch, statusFilter]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleStatusClick = (productId, status) => {
        const sureTxt =
            status === 1
                ? "Are you sure to change status to inactive?"
                : "Are you sure to change status to active?";
        const decTxt =
            status === 1
                ? "This will inactivate the product"
                : "This will activate the product";
        showConfirm(sureTxt, {
            description: decTxt,
            type: "info",
        }).then((answer) => {
            if (answer) {
                setProductId(productId);
                setProductStatus(status);
                if (status !== 1) {
                    submitUpdateDetails(productId, status);
                } else {
                    setOpenModal(true);
                }
            }
        });
    };
    const handleLoginClick = async (productId) => {
        setLoaderVisible(true);

        if (productId) {
            navigate(`/admin/products/addnew?id=${productId}`);
        }
        setLoaderVisible(false);
    }

    const columns = [
        { field: "sno", headerName: "#", width: 64, sortable: false, filterable: false },
        {
            field: "name",
            headerName: "Product Name",
            flex: 1.6,
            minWidth: 220,
            renderCell: (params) => <strong className="pm-product-name">{params.value || "—"}</strong>,
        },
        {
            field: "brandName",
            headerName: "Brand",
            flex: 1,
            minWidth: 140,
        },
        { field: "categoryName", headerName: "Category", flex: 1, minWidth: 150 },
        {
            field: "itemImage", headerName: "Product", width: 96, sortable: false, filterable: false, renderCell: (params) => (
                <div className="pm-image-wrap">
                    {params.value && params.value.split(",").length > 0 && (
                        <img
                            src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${params.value.split(",")[0]}`}
                            alt={params.value}
                            className="pm-product-image"
                        />
                    )}
                    {!params.value && <span>No image</span>}
                </div>
            ),
        },
        { field: "pro_created", headerName: "Created", minWidth: 170, flex: 0.9 },
        {
            field: "status",
            headerName: "Status",
            width: 120,
            renderCell: (params) => (
                        <button
                            onClick={() => handleStatusClick(params.id, parseInt(params.value))}
                            className={`pm-status ${params.value === 1 ? "pm-status--active" : "pm-status--inactive"}`}
                        >
                            {params.value === 1 ? "Active" : "Inactive"}
                        </button>
            ),
        },
        {
            field: "actions",
            headerName: "Action",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                    <div className="pm-actions">
                        <button
                            onClick={() => handleLoginClick(params.id)}
                            className="pm-action pm-action--edit"
                        >
                            <FontAwesomeIcon icon="fa-solid fa-pen" /> Edit
                        </button>
                        <button
                            className="pm-action pm-action--view"
                            onClick={() => openViewModel(params.id)}
                        >
                            <FontAwesomeIcon icon="fa-solid fa-eye" /> View
                        </button>
                    </div>
            ),
        },
    ];

    const openViewModel = (id) => {
        if (id !== null) {
            setProductId(id);
            setInvModal(true);
        }
    };

    const hideViewModal = () => {
        setProductId(null);
        setInvModal(false);
    };

    const validateCancelForm = () => {
        const newErrors = {};
        if (!closeNotes) newErrors.closeNotes = "Notes is required";
        return newErrors;
    };

    const handleInactivate = async (event) => {
        event.preventDefault();
        const formErrors = validateCancelForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
        } else {
            setErrors({});
            try {
                submitUpdateDetails(productId, productStatus, closeNotes);
            } catch (error) {
                setErrors({ common: "Update Failed" });
            }
        }
    };

    const submitUpdateDetails = async (productId, status, notes = null) => {
        try {
            let sendStatus = status === 1 ? 2 : 1;
            let params = {
                action: "status",
                status: sendStatus,
                productId: productId,
                reason: notes,
            };

            const resp = await productAPI.updateProductStatus(params,'product');
            if (resp) {
                const message = resp.message || "Product status updated successfully";
                showConfirm("Success", {
                    description: message,
                    type: "Success",
                    hideCancel: true,
                }).then(() => {
                    setRefreshTable(!refreshTable);
                    setOpenModal(false);
                });
            } else {
                const message = "Process failed, kindly try again later!";
                showConfirm("Oops!", {
                    description: message,
                    type: "warning",
                    hideCancel: true,
                }).then(() => {
                    setRefreshTable(!refreshTable);
                    setOpenModal(false);
                });
            }
            handleClose();
        } catch (error) {
            showConfirm("Oops!", {
                description: "Process failed, kindly try again later!",
                type: "warning",
                hideCancel: true,
            });
            handleClose();
        }
    };

    const setReason = (e) => {
        setCloseNotes(e.target.value);
    };

    const handleClose = () => {
        setOpenModal(false);
        setNotesText("Enter Reason");
    };

    const handleAddClick = () => {
        navigate("/admin/products/addnew");
    }
    const handleBulkAddClick = () => {
        navigate("/admin/products/addbulk");
    }

    return (
        <Container fluid className="pm-page">
            <DynamicBreadcrumb pathArray={pathArray} />
            <header className="pm-header">
                <div>
                    <span className="pm-eyebrow">CATALOGUE</span>
                    <h1>Product Management</h1>
                    <p>Search, review and manage your complete product catalogue.</p>
                </div>
                <div className="pm-header-actions">
                    <button className="pm-button pm-button--secondary" onClick={handleBulkAddClick}>
                        <FontAwesomeIcon icon="fa-solid fa-file-arrow-up" /> Bulk upload
                    </button>
                    <button className="pm-button pm-button--primary" onClick={handleAddClick}>
                        <FontAwesomeIcon icon="fa-solid fa-plus" /> Add product
                    </button>
                </div>
            </header>
            <section className="pm-summary">
                <div><span>Total results</span><strong>{totalProduct.toLocaleString("en-IN")}</strong></div>
                <div><span>Current page</span><strong>{rows.length}</strong></div>
                <div><span>Search status</span><strong>{debouncedSearch ? "Filtered" : "All products"}</strong></div>
            </section>
            {loaderVisible && <Loader />}
            <Box className="pm-table-card">
                <div className="pm-toolbar">
                    <div className="pm-search">
                        <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" />
                        <input
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            placeholder="Search product name, brand, category or item code..."
                            aria-label="Search products"
                        />
                        {searchText && <button type="button" onClick={() => setSearchText("")} aria-label="Clear search">×</button>}
                    </div>
                    <select value={statusFilter} onChange={(event) => setStatusFilter(Number(event.target.value))} aria-label="Filter by status">
                        <option value={0}>All statuses</option>
                        <option value={1}>Active</option>
                        <option value={2}>Inactive</option>
                    </select>
                </div>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    paginationMode="server"
                    rowCount={totalProduct}
                    loading={loading}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sortingMode="server"
                    onSortModelChange={setSortModel}
                    disableColumnFilter
                    onRowSelectionModelChange={(row) => {
                        setRowSelectionModel(row);
                    }}
                    rowSelectionModel={rowSelectionModel}
                    pageSizeOptions={[15, 25, 50, 100]}
                    disableRowSelectionOnClick
                    sx={{ [`& .${gridClasses.cell}`]: { py: 1 } }}
                />
            </Box>

            {productId > 0 && (
                <ProductViewModal
                    productId={productId}
                    invModal={invModal}
                    change={hideViewModal}
                />
            )}

            <Modal show={openModal} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title> Enter Reason</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form className="cancelForm">
                        <Form.Group className="mb-3" controlId="closeNotes">
                            <Form.Label>Enter inactivate reason</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={closeNotes}
                                onChange={setReason}
                                isInvalid={!!errors.closeNotes}
                            />
                        </Form.Group>
                        <Form.Control.Feedback type="invalid">
                            {errors.closeNotes}
                        </Form.Control.Feedback>
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="primary" onClick={handleInactivate}>
                        Submit Reason
                    </Button>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};


export default ProductsList;
